import { PaginatedResponse } from './common'

// Admin Adventure Types - API returns numeric values
// Status: 0=Draft, 1=Testing, 2=Published, 3=Archived
export type AdminAdventureStatus = number
// ModerationStatus: 0=Pending, 1=Approved, 2=Rejected, 3=Flagged
export type AdminModerationStatus = number

// Helper maps for display
export const ADVENTURE_STATUS_MAP: Record<number, string> = {
  0: 'Draft',
  1: 'Testing',
  2: 'Published',
  3: 'Archived',
}

export const MODERATION_STATUS_MAP: Record<number, string> = {
  0: 'Pending',
  1: 'Approved',
  2: 'Rejected',
  3: 'Flagged',
}

export interface AdminAdventureDto {
  id: string
  title: string
  quickDescription?: string
  status: AdminAdventureStatus
  moderationStatus: AdminModerationStatus
  categoryId: number
  difficultyLevel: number
  isPublic: boolean
  isRepeatable: boolean
  allowGroups: boolean
  location?: string
  latitude?: number | null
  longitude?: number | null
  creatorId: string
  creatorDisplayName: string
  ownerEntityId: string
  ownerEntityType: number
  ownerDisplayName: string
  createdDate: string
  updatedDate: string
  startDate?: string | null
  endDate?: string | null
  participantCount: number
  completionCount: number
  poiCount: number
  workflowVersionCount: number
  publishedWorkflowVersion?: number | null
  testingWorkflowVersion?: number | null
  latestWorkflowVersion: number
  thumbnailUrl?: string | null
}

export interface AdminAdventuresListParams {
  page?: number
  pageSize?: number
  search?: string
  status?: number
  moderationStatus?: number
  categoryId?: number
  creatorId?: string
  isPublic?: boolean
  createdAfter?: string
  createdBefore?: string
  sortBy?: 'Title' | 'CreatedDate' | 'UpdatedDate' | 'Status' | 'ParticipantCount' | 'CreatorUsername'
  sortDescending?: boolean
}

export interface AdminAdventureStats {
  totalCount: number
  draftCount: number
  publishedCount: number
  testingCount: number
  archivedCount: number
  pendingModerationCount: number
  publicCount: number
  privateCount: number
}

export type AdminAdventuresListResponse = PaginatedResponse<AdminAdventureDto>

// Admin User Types
export interface AdminUserDto {
  id: string
  email: string
  userName?: string
  displayName: string
  firstName?: string
  lastName?: string
  fullName?: string
  phoneNumber?: string
  avatar?: string
  profilePictureUrl?: string
  roles: string[]
  isActive: boolean
  isOnline?: boolean
  userFlags: number
  createdAt?: string
  lastLoginAt?: string
  lastSeen?: string
  updatedAt?: string
}

export interface AdminUsersListParams {
  page?: number
  pageSize?: number
  search?: string
  role?: string
  isActive?: boolean
  sortBy?: 'DisplayName' | 'Email' | 'CreatedAt' | 'LastLoginAt'
  sortDescending?: boolean
}

export type AdminUsersListResponse = PaginatedResponse<AdminUserDto>

// User Flags Management
export interface UpdateUserFlagsRequest {
  addFlags?: number    // Flags to add (bitwise OR)
  removeFlags?: number // Flags to remove (bitwise AND NOT)
}

export interface UpdateUserFlagsResponse {
  message: string
}

// User Status Management
export interface UpdateUserStatusRequest {
  isActive: boolean
}

// User Roles Management
export interface UpdateUserRolesRequest {
  roles: string[]
}

// User Connections (OAuth providers)
export interface UserConnectionDto {
  id: string
  provider: string
  providerUserId: string
  providerUsername?: string
  providerEmail?: string
  connectedAt: string
  lastUsedAt?: string
}

// Session Types
export type SessionStatus = 'InProgress' | 'Completed' | 'Failed' | 'Abandoned'

export interface AdminSessionDto {
  id: string
  adventureId: string
  adventureTitle: string
  userId: string
  userDisplayName: string
  userEmail?: string
  groupId?: string
  groupName?: string
  status: SessionStatus
  isTestSession: boolean
  createdAt: string
  startedAt?: string
  completedAt?: string
  abandonedAt?: string
  currentNodeId?: string
  repeatAfterFailure?: boolean
  cluesUsed: number
  locationLogCount: number
  nodeVisitCount: number
}

export interface AdminSessionDetailDto extends AdminSessionDto {
  userEmail: string
}

export interface AdminSessionsListParams {
  adventureId?: string
  userId?: string
  status?: SessionStatus
  createdAfter?: string
  createdBefore?: string
  isTestSession?: boolean
  sortBy?: 'CreatedAt' | 'StartedAt' | 'CompletedAt' | 'Status'
  sortDescending?: boolean
  page?: number
  pageSize?: number
}

export interface AdminSessionStatsDto {
  totalSessions: number
  inProgressCount: number
  completedCount: number
  failedCount: number
  abandonedCount: number
  testSessionCount: number
}

export interface SessionLocationLogDto {
  id: string
  sessionId: string
  latitude: number
  longitude: number
  accuracy?: number
  altitude?: number
  velocity?: number
  heading?: number
  timestamp: string
  distanceFromPrevious?: number
  totalDistanceTraveled?: number
}

export type AdminSessionsListResponse = PaginatedResponse<AdminSessionDto>

// Adventure Import Results
export interface AdventureImportResult {
  success: boolean
  newAdventureId: string | null
  errorMessage: string | null
  warnings: string[]
  idMappings: Record<string, string>
}

export interface AdventureBundleImportResult extends AdventureImportResult {
  mediaFilesImported: number
  shareCodesImported: number
  mediaFileMappings: Record<string, string>
  shareCodeMappings: Record<string, string>
}

export interface AdventureImportParams {
  importAsDraft?: boolean
  skipTeamMembers?: boolean
}

export interface AdventureBundleImportParams extends AdventureImportParams {
  importMediaLibrary?: boolean
  importShareCodes?: boolean
  regenerateQRCodes?: boolean
}

// Adventure Delete Result
export interface AdminAdventureDeleteResult {
  success: boolean
  message: string
  adventureId: string
  adventureTitle: string
  deletedItems: {
    sessionsDeleted: number
    poisDeleted: number
    workflowVersionsDeleted: number
    requirementsDeleted: number
    rewardsDeleted: number
    tagsDeleted: number
    ticketsDeleted: number
    userProgressDeleted: number
    discussionsDeleted: number
    mediaFilesDeleted: number
    mediaFileSizeBytesFreed: number
    mediaFileDeletionErrors: string[]
  }
}
